<script lang="ts">
  import { onMount } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { UPGRADES } from '../core/balance';
  import { autowalkRate, ceiling, medals, purchaseStatus, type GameEvent } from '../core/engine';
  import NumberLine from '../shared/components/NumberLine.svelte';
  import NumberReadout from '../shared/components/NumberReadout.svelte';
  import TrialPanel from '../shared/components/TrialPanel.svelte';
  import { sound } from '../shared/lib/audio';
  import { startFeedback } from '../shared/lib/feedback';
  import { fmt, fmtShort } from '../shared/lib/format';
  import { game } from '../shared/lib/game.svelte';
  import { settings } from '../shared/lib/settings.svelte';
  import Collection from '../v2/views/Collection.svelte';
  import Settings from '../v2/views/Settings.svelte';
  import { reveal } from './reveal.svelte';
  import Sheet from './Sheet.svelte';
  import Shop from './Shop.svelte';

  type Panel = 'shop' | 'collection' | 'settings' | null;
  let panel = $state<Panel>(null);
  let idle = $state(false);

  const s = $derived(game.s);
  const canBuy = $derived(
    UPGRADES.some((u) => {
      if (!reveal.upgrades.includes(u.id)) return false;
      const st = purchaseStatus(s, u.id);
      return st.kind === 'available' && st.affordable;
    }),
  );
  const toEdge = $derived(ceiling(s) - s.position);

  // Reveal rules run after every state change. A fresh save (new game or reset) folds the
  // interface back down to nothing.
  $effect(() => {
    if (s.taps === 0 && s.lifetimePoints === 0 && reveal.pieces.length > 0) reveal.reset();
    reveal.update(s);
  });

  $effect(() => {
    document.documentElement.dataset.palette = settings.palette;
    document.documentElement.classList.toggle('reduce-motion', settings.reducedMotion);
    sound.enabled = settings.sound;
    sound.setVolume(settings.volume);
    settings.persist();
  });

  // Whispers fade on their own after a few seconds.
  $effect(() => {
    const w = reveal.whisper;
    if (!w) return;
    const t = setTimeout(() => {
      if (reveal.whisper?.id === w.id) reveal.whisper = null;
    }, 4200);
    return () => clearTimeout(t);
  });

  function tap(x?: number, y?: number) {
    sound.unlock();
    game.tap();
    idle = false;
    if (x !== undefined && y !== undefined && !settings.reducedMotion) ripple(x, y);
  }

  function onStagePointer(e: PointerEvent) {
    if (e.button !== 0 || panel) return;
    if ((e.target as Element).closest('[data-ui], button, a, input, textarea')) return;
    e.preventDefault();
    tap(e.clientX, e.clientY);
  }

  function onKey(e: KeyboardEvent) {
    if (panel || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
    if ((e.target as Element).closest('input, textarea, button, [role="radio"]')) return;
    if (e.code === 'Space' || e.code === 'Enter') {
      e.preventDefault();
      tap();
    }
  }

  /** Soft rings from wherever you touched; each removes itself when its animation ends. */
  let ripples = $state<Array<{ id: number; x: number; y: number }>>([]);
  let rippleId = 0;
  function ripple(x: number, y: number) {
    ripples = [...ripples.slice(-6), { id: ++rippleId, x, y }];
  }
  const dropRipple = (id: number) => (ripples = ripples.filter((r) => r.id !== id));

  // Famous numbers and twins speak through the whisper line instead of toasts.
  function onEvents(events: readonly GameEvent[]) {
    for (const e of events) {
      if (e.type === 'famous')
        reveal.say(`${fmt(e.entries[0]!.n)}. ${e.entries[0]!.name.toLowerCase()}.`);
      else if (e.type === 'twin' && reveal.has('caption'))
        reveal.say(`${fmt(e.lo)} and ${fmt(e.hi)}. twins.`);
      else if (e.type === 'blocked') reveal.say('the line ends here. for now.');
    }
  }

  onMount(() => {
    const stopFeedback = startFeedback({ toasts: false });
    const off = game.on(onEvents);
    let raf = 0;
    let prev = performance.now();
    const loop = (now: number) => {
      game.frame(now - prev, now);
      prev = now;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const hint = setTimeout(() => (idle = game.s.taps === 0), 2500);
    const flush = () => game.save();
    const onVisibility = () => document.visibilityState === 'hidden' && flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(hint);
      stopFeedback();
      off();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  });

  const d = $derived(settings.reducedMotion ? 0 : 1);
  const appear = (node: Element) => fly(node, { y: 8, duration: 700 * d, opacity: 0 });
</script>

<svelte:window onkeydown={onKey} />

<div
  class="stage"
  onpointerdown={onStagePointer}
  role="application"
  aria-label="Tap anywhere to step"
>
  {#each ripples as r (r.id)}
    <span
      class="ripple"
      style:left="{r.x}px"
      style:top="{r.y}px"
      onanimationend={() => dropRipple(r.id)}
      aria-hidden="true"
    ></span>
  {/each}

  <div class="corner left">
    {#if reveal.has('medals')}
      <button
        class="stat"
        data-ui
        in:appear
        disabled={!reveal.has('collection')}
        onclick={() => (panel = 'collection')}
        aria-label="{fmt(medals(s))} prime medals"
      >
        <span class="dot"></span><span class="num">{fmtShort(medals(s))}</span>
      </button>
    {/if}
  </div>

  <div class="corner right">
    {#if reveal.has('points')}
      <span class="stat points" in:appear>
        <span class="num">{fmtShort(s.points)}</span><span class="unit">pts</span>
      </span>
    {/if}
  </div>

  <div class="center">
    <NumberReadout eyebrow="" caption={reveal.has('caption')} hero />
    <div class="whisper" aria-live="polite">
      {#key reveal.whisper?.id}
        {#if reveal.whisper}
          <p in:fade={{ duration: 500 * d }} out:fade={{ duration: 400 * d }}>
            {reveal.whisper.text}
          </p>
        {:else if idle}
          <p class="hint" in:fade={{ duration: 900 * d }}>tap anywhere</p>
        {/if}
      {/key}
    </div>
  </div>

  <div class="foot">
    {#if game.trial.phase !== 'off'}
      <div data-ui><TrialPanel /></div>
    {/if}

    {#if reveal.has('radar')}
      <div class="radar" data-ui in:appear><NumberLine /></div>
    {/if}

    <div class="chips">
      {#if reveal.has('edge') && toEdge >= 0 && toEdge <= 40 && s.direction === 1}
        <span class="chip quiet" in:appear>edge in <span class="num">{fmt(toEdge)}</span></span>
      {/if}
      {#if reveal.has('flip')}
        <button class="chip" in:appear onclick={() => game.flip()}>
          {s.direction === 1 ? '→ walking up' : '← walking down'}
        </button>
      {/if}
      {#if reveal.has('auto')}
        <span class="chip quiet" in:appear><span class="pulse"></span>auto {autowalkRate(s)}/s</span
        >
      {/if}
      {#if reveal.has('trial') && game.trial.phase === 'off'}
        <button class="chip" in:appear onclick={() => game.armTrial(game.trial.length)}
          >speed trial</button
        >
      {/if}
    </div>

    <nav class="links" aria-label="Menu">
      {#if reveal.has('shop')}
        <button in:appear onclick={() => (panel = 'shop')}>
          upgrades{#if canBuy}<span class="new" aria-hidden="true"></span>{/if}
        </button>
      {/if}
      {#if reveal.has('collection')}
        <button in:appear onclick={() => (panel = 'collection')}>collection</button>
      {/if}
      {#if reveal.has('menu')}
        <button in:appear onclick={() => (panel = 'settings')}>settings</button>
      {/if}
    </nav>
  </div>
</div>

{#if panel === 'shop'}
  <Sheet title="Upgrades" onclose={() => (panel = null)}><Shop /></Sheet>
{:else if panel === 'collection'}
  <Sheet title="Collection" onclose={() => (panel = null)}><Collection /></Sheet>
{:else if panel === 'settings'}
  <Sheet title="Settings" onclose={() => (panel = null)}><Settings /></Sheet>
{/if}

<style>
  .stage {
    position: relative;
    height: 100%;
    overflow: hidden;
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'left right'
      'center center'
      'foot foot';
    padding: calc(env(safe-area-inset-top, 0px) + 16px) 16px
      calc(env(safe-area-inset-bottom, 0px) + 12px);
    cursor: pointer;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }

  .corner {
    display: flex;
    min-height: 36px;
  }
  .left {
    grid-area: left;
  }
  .right {
    grid-area: right;
    justify-content: flex-end;
  }

  .stat {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 14px;
    border-radius: 999px;
    font-weight: 700;
    font-size: var(--text-md);
    background: var(--bg-sunken);
    cursor: default;
  }
  button.stat:not(:disabled) {
    cursor: pointer;
  }
  .stat .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--prime);
  }
  .points {
    color: var(--accent);
  }
  .unit {
    font-size: var(--text-xs);
    color: var(--ink-3);
    font-weight: 600;
  }

  .center {
    grid-area: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-3);
    min-height: 0;
  }

  .whisper {
    position: relative;
    height: 3em;
    width: min(32ch, 100%);
    text-align: center;
  }
  .whisper p {
    position: absolute;
    inset: 0;
    margin: 0;
    font-size: var(--text-sm);
    color: var(--ink-2);
    font-style: italic;
  }
  .whisper .hint {
    color: var(--ink-3);
    font-style: normal;
    letter-spacing: 0.08em;
    animation: breathe 2.4s ease-in-out infinite;
  }
  @keyframes breathe {
    50% {
      opacity: 0.35;
    }
  }

  .foot {
    grid-area: foot;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
    cursor: default;
  }
  .radar {
    cursor: default;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--space-2);
    min-height: 32px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid var(--line);
    font-size: var(--text-sm);
    color: var(--ink-2);
    background: var(--bg-raised);
  }
  .chip.quiet {
    background: transparent;
    border-color: transparent;
    color: var(--ink-3);
  }
  .pulse {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
    animation: breathe 1.6s ease-in-out infinite;
  }

  .links {
    display: flex;
    justify-content: center;
    gap: var(--space-5);
    min-height: 44px;
  }
  .links button {
    position: relative;
    padding: 10px 4px;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--ink-2);
    letter-spacing: 0.02em;
  }
  .links button:hover {
    color: var(--ink);
  }
  .new {
    position: absolute;
    top: 8px;
    right: -8px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--accent);
  }

  .ripple {
    position: fixed;
    width: 160px;
    height: 160px;
    border-radius: 50%;
    border: 1.5px solid var(--accent);
    pointer-events: none;
    opacity: 0;
    animation: ripple 650ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes ripple {
    from {
      transform: translate(-50%, -50%) scale(0.2);
      opacity: 0.45;
    }
    to {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0;
    }
  }
</style>
